import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  Input,
  Label,
  formatDate,
  formatNumber,
} from '@berky/ui'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useState } from 'react'

import { usePlatformUsageApi, type ModuleUsagePage } from './api'
import {
  berlinDateTime,
  moduleTitle,
  queryFilters,
  type UsageFilters,
} from './usage-utils'

export function UsageDetails({ filters }: { filters: UsageFilters }) {
  const api = usePlatformUsageApi()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState('')
  const usage = useQuery({
    queryKey: ['platform-usage', 'daily', filters, page, search],
    queryFn: ({ signal }) =>
      api.get<ModuleUsagePage>('/modules', {
        query: { ...queryFilters(filters), seite: page, suche: search || undefined },
        signal,
      }),
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tägliche Nutzung im Detail</CardTitle>
        <CardDescription>
          Eine Zeile je Person, Tag und Modul – einschließlich Einstiegen, erfolgreichen
          Aktionen und bearbeiteten Datensätzen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            setSearch(draft.trim())
            setPage(1)
          }}
        >
          <div className="grid flex-1 gap-1.5">
            <Label htmlFor="usage-detail-search">Person suchen</Label>
            <Input
              id="usage-detail-search"
              maxLength={320}
              value={draft}
              placeholder="Name oder E-Mail-Adresse"
              onChange={(event) => setDraft(event.target.value)}
            />
          </div>
          <Button variant="outline" type="submit">
            <Search aria-hidden="true" />
            Suchen
          </Button>
        </form>
        <DataTable
          rows={usage.data?.items ?? []}
          rowKey={(row) => `${row.usageDate}:${row.userId}:${row.moduleId}`}
          caption="Tägliche Nutzung je Person und Modul"
          isLoading={usage.isPending}
          pagination={usage.data}
          onPageChange={setPage}
          error={
            usage.isError
              ? {
                  description: 'Die Detailliste konnte nicht geladen werden.',
                  onRetry: () => void usage.refetch(),
                }
              : null
          }
          emptyState={
            <EmptyState
              title="Keine Nutzung gefunden"
              description="Für den gewählten Zeitraum und Bereich gibt es keine Einträge."
            />
          }
          columns={[
            { key: 'date', header: 'Datum', cell: (row) => formatDate(row.usageDate) },
            {
              key: 'person',
              header: 'Nutzer',
              cell: (row) => (
                <div className="min-w-48">
                  <p className="font-medium">{row.userName}</p>
                  <p className="text-xs text-muted-foreground">{row.userEmail}</p>
                </div>
              ),
            },
            {
              key: 'module',
              header: 'Modul',
              cell: (row) => moduleTitle(row.moduleId),
            },
            {
              key: 'entries',
              header: 'Einstiege',
              align: 'right',
              cell: (row) => formatNumber(row.usageCount),
            },
            {
              key: 'lastAccess',
              header: 'Letzter Zugriff',
              cell: (row) => berlinDateTime(row.lastUsedAt),
            },
            {
              key: 'actions',
              header: 'Aktionen',
              align: 'right',
              cell: (row) => formatNumber(row.actionCount),
            },
            {
              key: 'records',
              header: 'Datensatzänderungen',
              align: 'right',
              cell: (row) => formatNumber(row.changedRecordCount),
            },
            {
              key: 'lastAction',
              header: 'Letzte Aktion',
              cell: (row) =>
                row.lastActionAt ? (
                  berlinDateTime(row.lastActionAt)
                ) : (
                  <span className="text-muted-foreground">–</span>
                ),
            },
          ]}
        />
        <p className="text-xs text-muted-foreground">
          Aktionen erscheinen, sobald das jeweilige Modul erfolgreiche Fachvorgänge
          meldet. Inhalte und Datensatzkennungen werden nicht gespeichert.
        </p>
      </CardContent>
    </Card>
  )
}
